/* eslint-disable prettier/prettier */
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';

import { ENV } from '@environment';
import { Formik, FormikValues, Form } from 'formik';
import { RootState } from 'store';
import * as yup from 'yup';

import { setUserCurrentLoyalty } from 'action-creators/user';
import BottomSheet from 'components/BottomSheet';
import Button from 'components/Buttons/Button';
import SimpleAlert from 'components/SimpleAlert';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useUserAccount from 'hooks/useUserAccount';
import { CostCenter } from 'models/costCenter';
import { ProductType } from 'models/enums/productType';
import { FlightValidationResponse } from 'models/flightValidation';
import { Price } from 'models/price';
import { UserCurrentLoyalty } from 'reducers/user';
import ConfirmProfile from 'screens/components/Booking/ConfirmProfile';
import TravelJustify from 'screens/components/TravelJustify';
import { AddToCartEvent, amplitudelogEvent } from 'services/amplitudeService';
import { getCostCenters } from 'services/costCenterService';
import { Alliance, fetchGetAllAlliances, getAllLoyaltyByUser, Loyalty } from 'services/loyaltiesService';
import { BreakRulesReason } from 'services/travelBreakRulesReasonService';
import { Reason } from 'services/travelReasonService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { daysCount } from 'utils/generic';

import {
  Checkout,
  ConfirmButton,
  ChurnContainer,
  SupportAvatar,
  ChurnTitle,
  ChurnDetail,
  CancelButton,
} from './styles';

interface FlightForm {
  costCenter?: Reason;
  justifyForBreakingRules: string;
  reason?: Reason;
  breakRulesReason?: BreakRulesReason;
}

interface Props {
  loading: boolean;
  price: Price;
  isPreApproved: boolean;
  isBestOption: boolean;
  onConfirm: (
    justifyForBreakingRules: string,
    reason: string,
    costCenterId?: string,
    breakRulesReasonId?: string,
  ) => void;
  onClose: () => void;
  showChurning: boolean;
  setShowChurning: (show: boolean) => void;
  details?: FlightValidationResponse;
}

const DetailsCheckout = ({ loading, price, onConfirm, onClose, showChurning, setShowChurning, details }: Props) => {
  const { t } = useStoreTranslation();
  const [showConfirmProfile, setShowConfirmProfile] = useState(false);
  const [showJustify, setShowJustify] = useState(false);
  const userAccount = useUserAccount();
  const { traveler, departurePoint, arrivalPoint } = useSelector((state: RootState) => state.flights);
  const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
  const [loyaltyList, setLoyaltyList] = useState<Loyalty[] | undefined>(undefined);
  const [allianceList, setAllianceList] = useState<Alliance[] | undefined>(undefined);
  const isInternational = useMemo(() => departurePoint?.country !== arrivalPoint?.country, [
    departurePoint,
    arrivalPoint,
  ]);
  const history = useHistory();
  const search = useSelector((state: RootState) => state.flights);
  const dispatch = useDispatch();
  const flightTraveler = useSelector((state: RootState) => state.flights.traveler);

  const initialValues: FlightForm = useMemo(
    () =>
      costCenters && costCenters.length > 0
        ? { justifyForBreakingRules: '', reason: undefined, costCenter: undefined, breakRulesReason: undefined }
        : {
            justifyForBreakingRules: '',
            reason: undefined,
            breakRulesReason: undefined,
          },
    [costCenters],
  );

  useEffect(() => {
    getCostCenters().then((res) => {
      if (res && res.length > 0) {
        const centers = res.filter((c) => c.isActive);
        if (centers.length > 0) setCostCenters(centers);
      }
    });
  }, [t]);

  const fetchAlliancesAndLoyalties = async () => {
    const alliances = await fetchGetAllAlliances();
    setAllianceList(alliances);

    const loyalties = await getAllLoyaltyByUser();
    setLoyaltyList(loyalties);
  };

  useEffect(() => {
    if (flightTraveler?.id === userAccount?.id || flightTraveler?.userId === userAccount?.id) {
      fetchAlliancesAndLoyalties();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateUserLoyalty = () => {
    if (allianceList !== undefined && loyaltyList !== undefined) {
      const airlineCode = details?.tripOption.sectors.first().flights.first().airlineCode ?? '';
      const currentLoyalty = loyaltyList?.filter((value) => value.loyaltyId === airlineCode).first();
      if (currentLoyalty) {
        dispatch(
          setUserCurrentLoyalty({
            loyaltyNumber: currentLoyalty.loyaltyNumber,
            loyaltyName: currentLoyalty.loyaltyName,
            airlineCode: airlineCode ?? '',
          } as UserCurrentLoyalty),
        );
      } else {
        const newLoyalty = allianceList?.filter((value) => value.id === airlineCode).first();
        if (newLoyalty) {
          dispatch(
            setUserCurrentLoyalty({
              loyaltyNumber: '',
              loyaltyName: newLoyalty.name,
              airlineCode: airlineCode ?? '',
            } as UserCurrentLoyalty),
          );
        } else {
          dispatch(
            setUserCurrentLoyalty({
              loyaltyNumber: '',
              loyaltyName: '',
              airlineCode: airlineCode ?? '',
            } as UserCurrentLoyalty),
          );
        }
      }
    }
  };

  useEffect(() => {
    updateUserLoyalty();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allianceList, loyaltyList]);

  const handleSubmit = ({ justifyForBreakingRules, reason, costCenter, breakRulesReason }: FlightForm) => {
    if (reason) onConfirm(justifyForBreakingRules, reason.id, costCenter?.id, breakRulesReason?.id);
  };

  const validationSchema = useMemo(
    () =>
      costCenters && costCenters.length > 0
        ? yup.object().shape({
            justifyForBreakingRules: yup.string().min(10).max(300).trim().required(),
            reason: yup.object().required(),
            costCenter: yup.object().required(),
          })
        : yup.object().shape({
            justifyForBreakingRules: yup.string().min(10).max(300).trim().required(),
            reason: yup.object().required(),
          }),
    [costCenters],
  );

  const amplitudeAddToCartEvent: AddToCartEvent = {
    INDEX: 0,
    SOURCE: 'LIST',
    TRAVEL_PRODUCT_TYPE: 'FLIGHTS',
    TRAVEL_FROM: search?.departurePoint?.code ?? '',
    TRAVEL_TO: search?.arrivalPoint?.code ?? '',
    TRAVEL_DESTINATION_TYPE: '',
    TRAVEL_TRIP_TYPE: '',
    TRAVEL_ADVANCED_PURCHASE: daysCount(new Date(), search?.departureDate).toString(),
    TRAVEL_DATE_FROM: search?.departureDate?.toString() ?? '',
    TRAVEL_DATE_TO: search?.returnDate?.toString() ?? '',
    TRAVEL_TRIP_LENGTH: (daysCount(search?.departureDate, search?.returnDate) + 1).toString(),
    TRAVEL_PASSENGERS: '1',
    TRAVEL_ADULTS: '1',
    TRAVEL_CHILDREN: '0',
    TRAVEL_INFANTS: '0',
    TRAVEL_CLASS_OF_SERVICE: '',
    TRAVEL_ROOMS: '1',
    TRAVEL_WITH_BAGGAGE: '',
    TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
    SEARCHID: search?.search?.searchId ?? '',
    TRAVEL_PRODUCT_CODE: details?.tripOption.sectors.first().flights.first().airlineCode ?? '',
    TRAVEL_PRODUCT_NAME: details?.tripOption.sectors.first().flights.first().airlineName ?? '',
    TRAVEL_SUB_PRODUCT: details?.tripOption.sectors.first().flights.first().fareFamilies ?? '',
    TRAVEL_PRODUCT_CATEGORY: details?.tripOption.sectors.first().flights.first().cabin ?? '',
    TRAVEL_PRODUCT_PRICE: '',
    TRAVEL_PRODUCT_PROVIDER: details?.tripOption.provider ?? '',
    TRAVEL_FARE_TYPE: '',
  };

  const sendAddToCartEvent = () => {
    amplitudelogEvent(amplitudeEvents.ADD_TO_CART, amplitudeAddToCartEvent, userAccount);
  };

  if (!userAccount) return null;

  return (
    <>
      <Checkout>
        {userAccount?.bookingAllowed ? (
          <>
            <ConfirmButton
              onClick={() => {
                sendAddToCartEvent();
                setShowConfirmProfile(true);
              }}
              testId="flight-details-select-button"
            >
              {t('flightDetails.selectButton')}
            </ConfirmButton>
          </>
        ) : (
          <SimpleAlert title={t('bookingNotAllowedAlert.title')} content={t('bookingNotAllowedAlert.content')} />
        )}
      </Checkout>

      <ConfirmProfile
        show={showConfirmProfile}
        productType={ProductType.Flight}
        onClose={() => setShowConfirmProfile(false)}
        onConfirm={() => setShowJustify(true)}
        user={
          traveler
            ? { ...traveler, nationality: traveler.country, userId: traveler.id || traveler.userId }
            : { userId: userAccount.id, ...userAccount }
        }
        isInternationalFlight={isInternational}
        onSaveLoyaltyProgram={fetchAlliancesAndLoyalties}
      />

      <Formik
        onSubmit={handleSubmit}
        initialValues={initialValues}
        validationSchema={validationSchema}
        isInitialValid={(formik: FormikValues) => validationSchema.isValidSync(formik.initialValues)}
      >
        {({ isValid, handleSubmit: submit }: FormikValues) => (
          <Form>
            <TravelJustify
              show={showJustify}
              onClose={() => setShowJustify(false)}
              isValid={isValid}
              loading={loading}
              itineraryValue={price.total}
              currencyCode={price.currencyCode}
              onSubmit={submit}
              productType={ProductType.Flight}
              costCenters={costCenters}
              breakRulesMessage={details?.breakRulesMessage}
            />
          </Form>
        )}
      </Formik>

      <BottomSheet show={showChurning} modal noHeader onClose={() => {}}>
        <ChurnContainer>
          <SupportAvatar src="/images/support-avatar.png" />
          <ChurnTitle>{t('flightDetails.churn.title')}</ChurnTitle>
          <ChurnDetail>{t('flightDetails.churn.detail')}</ChurnDetail>

          <Button onClick={() => history.replace('/travelRequested')}>
            {t('flightDetails.churn.viewReservations')}
          </Button>
          <CancelButton
            onClick={() => {
              setShowChurning(false);
              onClose();
            }}
          >
            {t('flightDetails.churn.selectAnotherOption')}
          </CancelButton>
        </ChurnContainer>
      </BottomSheet>
    </>
  );
};

export default DetailsCheckout;
